import { useMemo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { Box, Tooltip, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LineButton from "../../components/buttons/LineButton";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

const STORAGE_KEY = "sapore.dashboard.layouts.v2";
const DRAG_HANDLE_CLASS = "dashboard-drag-handle";

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLUMNS = { lg: 12, md: 12, sm: 6, xs: 1, xxs: 1 };
const ROW_HEIGHT = 40;
const GRID_MARGIN = [20, 20];

function loadSavedLayouts() {
    try {
        const storedLayouts = localStorage.getItem(STORAGE_KEY);
        return storedLayouts ? JSON.parse(storedLayouts) : null;
    } catch {
        return null;
    }
}

function persistLayouts(layouts) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(layouts));
    } catch {
        // localStorage may be unavailable (private mode / quota); the arrangement
        // simply won't persist, which is acceptable degradation.
    }
}

// Each chart renders its own FormSection card; flatten it (transparent, no padding/margin/radius)
// and hide its duplicate internal title, since the tile already provides the card and a fixed title.
const FLATTENED_INNER_CARD = {
    margin: 0,
    padding: 0,
    backgroundColor: "transparent",
    borderRadius: 0,
};

// The content keeps its natural height and the tile scrolls if it doesn't fit. overflowX stays hidden
// so recharts' ResizeObserver reflows cleanly on resize; wide tables (scrollableX) opt into horizontal scroll.
function chartBodyStyle(item) {
    return {
        flex: 1,
        minHeight: 0,
        overflowX: item.scrollableX ? "auto" : "hidden",
        overflowY: "auto",
        "& > section": { ...FLATTENED_INNER_CARD, minHeight: "100%" },
        "& > section > .MuiTypography-h3": { display: "none" },
    };
}

function DragHandle() {
    return (
        <Tooltip title="Arrastrar para reacomodar">
            <Box
                className={DRAG_HANDLE_CLASS}
                sx={{
                    position: "absolute",
                    top: 14,
                    right: 16,
                    zIndex: 5,
                    display: "flex",
                    alignItems: "center",
                    cursor: "grab",
                    color: "text.secondary",
                    opacity: 0.5,
                    "&:hover": { opacity: 1 },
                    "&:active": { cursor: "grabbing" },
                }}
            >
                <DragIndicatorIcon fontSize="small" />
            </Box>
        </Tooltip>
    );
}

function DashboardChartsGrid({ items, leadingAction }) {
    const defaultLayout = useMemo(
        () => items.map((item) => ({ ...item.layout, i: item.key })),
        [items]
    );
    const [layouts, setLayouts] = useState(() => loadSavedLayouts() || { lg: defaultLayout });

    const handleLayoutChange = (_currentLayout, allLayouts) => {
        setLayouts(allLayouts);
        persistLayouts(allLayouts);
    };

    const settleResponsiveChildren = () => {
        window.dispatchEvent(new Event("resize"));
    };

    const restoreDefaultArrangement = () => {
        const restored = { lg: defaultLayout };
        setLayouts(restored);
        persistLayouts(restored);
    };

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {leadingAction && (
                        <Typography variant="body2" color="text.secondary">
                            Ver todo por
                        </Typography>
                    )}
                    {leadingAction}
                </Box>
                <LineButton onClick={restoreDefaultArrangement}>
                    <RestartAltIcon fontSize="small" />
                    Restablecer disposición
                </LineButton>
            </Box>

            <ResponsiveGridLayout
                className="layout"
                layouts={layouts}
                breakpoints={BREAKPOINTS}
                cols={COLUMNS}
                rowHeight={ROW_HEIGHT}
                margin={GRID_MARGIN}
                containerPadding={[0, 0]}
                draggableHandle={`.${DRAG_HANDLE_CLASS}`}
                resizeHandles={["se"]}
                onLayoutChange={handleLayoutChange}
                onResize={settleResponsiveChildren}
                onResizeStop={settleResponsiveChildren}
            >
                {items.map((item) => (
                    <Box key={item.key} sx={{ position: "relative" }}>
                        <DragHandle />
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: (theme) => theme.brand.field,
                                borderRadius: "20px",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1, pl: "1rem", pt: "1rem", pb: "0.5rem", pr: "2.5rem" }}
                            >
                                <Typography variant="h3" sx={{ fontSize: "1rem", flexGrow: 1 }}>
                                    {item.title}
                                </Typography>
                                {item.headerAction}
                            </Box>
                            <Box sx={chartBodyStyle(item)}>{item.element}</Box>
                        </Box>
                    </Box>
                ))}
            </ResponsiveGridLayout>
        </Box>
    );
}

export default DashboardChartsGrid;
